import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatTableModule } from '@angular/material/table';
import { filesize } from 'filesize';
import { merge } from 'lodash-es';
import {
  catchError,
  filter,
  map,
  shareReplay,
  take,
  takeUntil,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { AvatarComponent } from '../../../../components/avatar/avatar.component';
import { BaseDialog } from '../../../../components/dialog/base-dialog';
import { ShortTableComponent } from '../../../../components/short-table/short-table.component';
import { FilesizePipe } from '../../../../pipes/filesize.pipe';
import { showSaveFilePicker } from '../../../../utils/file';
import { Progress } from '../../../../utils/progress';
import { FileUploadDialogData, UploadFile } from '../../models/file-upload.model';

@Component({
  selector: 'app-file-upload-dialog',
  imports: [
    MatButton,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatIcon,
    MatIconButton,
    MatProgressBar,
    FilesizePipe,
    AvatarComponent,
    FilesizePipe,
    MatTableModule,
    MatTooltip,
    ShortTableComponent,
  ],
  templateUrl: './file-upload-dialog.component.html',
  styleUrl: './file-upload-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileUploadDialogComponent
  extends BaseDialog<FileUploadDialogData>
  implements OnDestroy
{
  protected readonly files = signal<UploadFile[]>([]);

  protected readonly hasFiles = computed(() => this.files().length > 0);

  protected readonly isUploading = computed(
    () => this.files().filter((file) => file.uploading).length > 0
  );

  protected readonly submitted = signal(false);
  protected readonly dialogRef = inject(MatDialogRef<FileUploadDialogComponent>);

  protected readonly showExtensionWarning = signal(false);
  protected readonly hadInvalidUploadsFromExtension = signal(false);

  protected readonly showSizeWarning = signal(false);
  protected readonly attemptedInvalidUploadSizes = signal<
    { maxSize: string; type: string }[]
  >([]);

  constructor() {
    super();

    effect(() => {
      this.dialogRef.disableClose = this.isUploading();
    });
  }

  ngOnDestroy() {
    this.files()
      .filter(
        (file): file is UploadFile & { previewUrl: string } =>
          file.previewUrl != undefined
      )
      .forEach((file) => URL.revokeObjectURL(file.previewUrl));
  }

  handleClick() {
    if (this.isUploading()) {
      return;
    }

    const extensions = this.data.allowedExtensions;

    showSaveFilePicker({
      extensions,
      multiple: true,
    })
      .pipe(
        filter((files) => files != undefined),
        map((files) => files.filter((file) => this.isFileAllowed(file))),
        map((files) => files.map((file) => this.mapToUploadFile(file))),
        tap((files) => this.addFile(files))
      )
      .subscribe();
  }

  getFileExtension(file: File) {
    const fileName = file.name;
    const lastDotIndex = fileName.lastIndexOf('.');

    if (lastDotIndex <= 0 || lastDotIndex === fileName.length - 1) {
      return null;
    }

    return fileName.substring(lastDotIndex);
  }

  isFileAllowed(file: File) {
    const extension = this.getFileExtension(file);

    if (!extension) {
      return false;
    }

    const typeAllowed =
      this.data.allowedMimeTypes.includes(file.type) ||
      this.data.allowedExtensions.includes(extension);

    if (!typeAllowed) {
      this.showExtensionWarning.set(true);
      this.hadInvalidUploadsFromExtension.set(true);
      console.warn(`File type not allowed: ${file.name} (${file.type})`);

      return false;
    }

    const maxSize = this.data.maxSizeMap.get(file.type);

    if (maxSize && file.size > maxSize) {
      this.showSizeWarning.set(true);
      this.attemptedInvalidUploadSizes.update((currentState) => {
        if (currentState.some((item) => item.type === file.type)) return currentState;

        return [...currentState, { type: file.type, maxSize: filesize(maxSize) }];
      });
      console.warn(
        `File ${file.name} is too big. Max: ${filesize(maxSize)}, actual: ${filesize(file.size)}`
      );

      return false;
    }

    return true;
  }

  handleDrop(event: DragEvent) {
    event.preventDefault();

    if (!event.dataTransfer || this.isUploading()) {
      return;
    }

    const fileList = event.dataTransfer.files;

    const uploadFiles = [...fileList]
      .filter((file) => this.isFileAllowed(file))
      .map((file) => this.mapToUploadFile(file));

    this.addFile(uploadFiles);
  }

  addFile(fileOrFiles: UploadFile | UploadFile[]) {
    this.files.update((currentState) => [
      ...currentState,
      ...(Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles]),
    ]);
  }

  removeFile(file: UploadFile) {
    if (file.previewUrl) {
      URL.revokeObjectURL(file.previewUrl);
    }

    this.files.update((currentState) => currentState.filter((f) => f.id !== file.id));
  }

  mapToUploadFile(file: File): UploadFile {
    const fileId = crypto.randomUUID();
    const previewUrl = this.isImageFile(file) ? URL.createObjectURL(file) : undefined;

    return {
      id: fileId,
      file,
      progress: new Progress(),
      uploading: false,
      error: false,
      previewUrl,
      setState: (newState) =>
        this.files.update((currentState) =>
          currentState.map((f) => {
            if (f.id === fileId) {
              return {
                ...f,
                ...newState,
              };
            }

            return f;
          })
        ),
      remove: () =>
        this.files.update((currentState) =>
          currentState.filter((f) => f.id !== fileId)
        ),
    };
  }

  isImageFile(file: File) {
    return file.type.startsWith('image/');
  }

  getFileIcon(file: File) {
    const type = file.type;

    if (type.startsWith('image/')) return 'image';
    if (type.startsWith('video/')) return 'movie';
    if (type.startsWith('audio/')) return 'music_note';

    return 'draft';
  }

  override handleConfirm(): void {
    this.submitted.set(true);

    // Clear error state on all files before retrying
    this.files.update((currentState) =>
      currentState.map((f) => (f.error ? { ...f, error: false } : f))
    );

    const files = this.files();

    const callback$ = this.data.onConfirm(files);

    const source$ = callback$.pipe(
      shareReplay(1),
      catchError((error) => {
        this.submitted.set(false);

        return throwError(() => error);
      })
    );

    const loading$ = merge(
      timer(1000).pipe(
        map(() => true),
        takeUntil(source$)
      ),

      source$.pipe(map(() => false))
    );

    loading$
      .pipe(
        filter((state) => !state),
        tap(() => this.submitted.set(false)),
        take(1)
      )
      .subscribe();
  }

  protected hideWarning(warningName: `extension` | `filesize`): void {
    if (warningName === `extension`) this.showExtensionWarning.set(false);
    else if (warningName === `filesize`) this.showSizeWarning.set(false);
  }
}
